import { useEffect, useState } from "react";
import { confirmationsService } from "../services/confirmations.service";

function isPresignedUrl(url: string): boolean {
  return url.includes("X-Amz-");
}

/**
 * Returns a presigned URL for the confirmation attachment so the image can load from S3.
 * If the stored URL is already presigned, returns it. Otherwise fetches from the API.
 */
export function usePresignedAttachmentUrl(
  confirmationId: string,
  storedUrl: string | undefined
): { url: string | null; loading: boolean; error: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!storedUrl) {
      queueMicrotask(() => {
        if (!cancelled) {
          setUrl(null);
          setLoading(false);
          setError(false);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    if (isPresignedUrl(storedUrl)) {
      queueMicrotask(() => {
        if (!cancelled) {
          setUrl(storedUrl);
          setLoading(false);
          setError(false);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    queueMicrotask(() => {
      if (!cancelled) {
        setLoading(true);
        setError(false);
      }
    });
    void confirmationsService
      .getAttachmentPresignedUrl(confirmationId)
      .then((presignedUrl) => {
        if (!cancelled) {
          setUrl(presignedUrl);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [confirmationId, storedUrl]);

  return { url, loading, error };
}
